import * as React from "react";
import {
  Box,
  Stack,
  Tooltip,
  IconButton,
  Button,
  FormControl,
  InputAdornment,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Popover,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import {
  buildFilename,
  exportToExcel,
  printWithFilename,
  PrintColumn,
  PAType,
} from "../../utils/listExport";

// ── Types ─────────────────────────────────────────────────────────────────────

export type LegendItem = { abbr: string; full: string };

export type ListPageToolbarProps<TRow extends object> = {
  // Search
  searchValue: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;

  // Communities multi-select
  selectedCommunities: string[];
  onCommunityChange: (event: SelectChangeEvent<string[]>) => void;
  onCommunityDelete: (val: string) => void;
  communitySearch: string;
  onCommunitySearchChange: (v: string) => void;
  onCommunityClose: () => void;
  filteredSubdivisions: string[];

  // Stage select
  stage: string;
  onStageChange: (event: SelectChangeEvent<string>) => void;

  // Clear
  hasActiveFilters: boolean;
  onClearFilters: () => void;

  // Right-side actions
  paType: PAType;
  createHref: string;
  filteredRows: TRow[];
  printColumns: PrintColumn<TRow>[];
  filterSummaryLines: string[];

  // Optional legend
  legendItems?: LegendItem[];
};

// ── Legend Popover ─────────────────────────────────────────────────────────────

function LegendPopover({ items }: { items: LegendItem[] }) {
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);

  return (
    <>
      <Tooltip title="Column legend">
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ color: "text.secondary" }}
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            p: 2,
            minWidth: 320,
            borderRadius: 2,
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: 1, color: "#374151", letterSpacing: 0.5 }}
        >
          Column Reference
        </Typography>
        <Divider sx={{ mb: 1.5 }} />

        {items.map(({ abbr, full }) => (
          <Box
            key={abbr}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              py: 0.5,
              "&:not(:last-child)": { borderBottom: "1px solid #f3f4f6" },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                minWidth: 120,
                fontFamily: "monospace",
                fontSize: "0.75rem",
                lineHeight: 1.6,
              }}
            >
              {abbr}
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b7280", lineHeight: 1.6 }}>
              {full}
            </Typography>
          </Box>
        ))}
      </Popover>
    </>
  );
}

// ── Shared button styles ───────────────────────────────────────────────────────

const outlinedBtnSx = {
  borderColor: "#d1d5db",
  color: "#6b7280",
  textTransform: "none",
  height: 40,
  whiteSpace: "nowrap",
  "&:hover": { borderColor: "#9ca3af", backgroundColor: "#f9fafb" },
} as const;

// ── Component ─────────────────────────────────────────────────────────────────

export function ListPageToolbar<TRow extends object>({
  searchValue,
  onSearchChange,
  selectedCommunities,
  onCommunityChange,
  onCommunityDelete,
  communitySearch,
  onCommunitySearchChange,
  onCommunityClose,
  filteredSubdivisions,
  stage,
  onStageChange,
  hasActiveFilters,
  onClearFilters,
  paType,
  createHref,
  filteredRows,
  printColumns,
  filterSummaryLines,
  legendItems,
}: ListPageToolbarProps<TRow>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "stretch", sm: "center" },
        gap: 1.5,
        mb: 2,
      }}
    >
      {/* ── Left controls ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", flex: 1 }}>

        {/* Search */}
        <TextField
          size="small"
          label="Search"
          variant="outlined"
          value={searchValue}
          onChange={onSearchChange}
          placeholder="Search..."
          sx={{ flex: { xs: "1 1 100%", sm: "0 1 180px" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" sx={{ color: (t) => t.palette.primary.main }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Communities */}
        <FormControl
          size="small"
          sx={{ flex: { xs: "1 1 calc(50% - 6px)", sm: "0 1 220px" }, maxWidth: 320 }}
        >
          <InputLabel id="toolbar-communities-label">Communities</InputLabel>
          <Select
            labelId="toolbar-communities-label"
            multiple
            value={selectedCommunities}
            onChange={onCommunityChange}
            input={<OutlinedInput label="Communities" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {(selected as string[]).map((val) => (
                  <Chip
                    key={val}
                    label={val}
                    size="small"
                    onDelete={() => onCommunityDelete(val)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                ))}
              </Box>
            )}
            MenuProps={{
              autoFocus: false,
              PaperProps: { style: { maxHeight: 340 } },
            }}
            onClose={onCommunityClose}
          >
            {/* Sticky search inside dropdown */}
            <MenuItem
              disableRipple
              disableTouchRipple
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "background.paper",
                pt: 1,
                pb: 0.5,
                px: 1,
                "&:hover": { backgroundColor: "background.paper" },
                "&.Mui-focusVisible": { backgroundColor: "background.paper" },
              }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Search communities..."
                value={communitySearch}
                autoFocus
                onChange={(e) => onCommunitySearchChange(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </MenuItem>

            {filteredSubdivisions.length === 0 ? (
              <MenuItem disabled>
                <ListItemText
                  primary="No communities found"
                  primaryTypographyProps={{ color: "text.secondary", fontSize: 13 }}
                />
              </MenuItem>
            ) : (
              filteredSubdivisions.map((sub) => (
                <MenuItem key={sub} value={sub}>
                  <Checkbox checked={selectedCommunities.includes(sub)} />
                  <ListItemText primary={sub} />
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Stage */}
        <FormControl
          size="small"
          sx={{ flex: { xs: "1 1 calc(50% - 6px)", sm: "0 0 120px" } }}
        >
          <InputLabel id="toolbar-stage-label">Stage</InputLabel>
          <Select
            labelId="toolbar-stage-label"
            value={stage}
            label="Stage"
            onChange={onStageChange}
          >
            <MenuItem value="All">All</MenuItem>
            {[...Array(11)].map((_, i) => (
              <MenuItem key={i} value={i.toString().padStart(2, "0")}>
                {i.toString().padStart(2, "0")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Clear filters */}
        <Tooltip title="Clear all filters">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={!hasActiveFilters}
              onClick={onClearFilters}
              startIcon={<FilterAltOffIcon fontSize="small" />}
              sx={{
                ...outlinedBtnSx,
                "&.Mui-disabled": { borderColor: "#e5e7eb", color: "#d1d5db" },
              }}
            >
              {isMobile ? "Clear" : "Clear filters"}
            </Button>
          </span>
        </Tooltip>

        {/* Legend (optional) */}
        {legendItems && legendItems.length > 0 && (
          <LegendPopover items={legendItems} />
        )}
      </Box>

      {/* ── Right controls ── */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>

        {/* Print */}
        <Tooltip title="Print table">
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => printWithFilename(paType)}
            startIcon={<PrintIcon fontSize="small" />}
            sx={(theme) => ({
              textTransform: "none",
              height: 40,
              whiteSpace: "nowrap",
              borderColor: `${theme.palette.primary.main} !important`,
              color: `${theme.palette.primary.main} !important`,
              backgroundColor: "#fff !important",
              "&:hover": {
                borderColor: `${theme.palette.primary.dark} !important`,
                backgroundColor: `${theme.palette.action.hover} !important`,
              },
              "& .MuiButton-startIcon": { color: `${theme.palette.primary.main} !important` },
            })}
          >
            Print
          </Button>
        </Tooltip>

        {/* Export */}
        <Tooltip title={`Export to Excel — ${buildFilename(paType, "xlsx")}`}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() =>
              exportToExcel(
                paType,
                filteredRows,
                printColumns.map((c) => ({
                  field: c.field,
                  headerName: c.label,
                  valueGetter: (_params: unknown, row: TRow) => c.getValue(row),
                })),
                filterSummaryLines
              )
            }
            startIcon={<FileDownloadIcon fontSize="small" />}
            sx={(theme) => ({
              textTransform: "none",
              height: 40,
              whiteSpace: "nowrap",
              borderColor: `${theme.palette.primary.main} !important`,
              color: `${theme.palette.primary.main} !important`,
              backgroundColor: "#fff !important",
              "&:hover": {
                borderColor: `${theme.palette.primary.dark} !important`,
                backgroundColor: `${theme.palette.action.hover} !important`,
              },
              "& .MuiButton-startIcon": { color: `${theme.palette.primary.main} !important` },
            })}
          >
            Export
          </Button>
        </Tooltip>

        {/* Create PA */}
        <Button
          disableElevation
          variant="contained"
          href={createHref}
          startIcon={<AddIcon />}
          sx={{
            whiteSpace: "nowrap",
            alignSelf: { xs: "stretch", sm: "auto" },
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          Create PA
        </Button>
      </Stack>
    </Box>
  );
}
